import { Texture, MeshPhongMaterial } from "three"
import { max } from "lodash"
import { GroundLabelMaterialOptions, ResolveLabelScaleOptions } from "./types"

export const getGroundLabelMaterial = (
  text: string,
  options: GroundLabelMaterialOptions
) => {
  const {
    fontSize: requestedFontSize,
    minLabelFontSize,
    maxLabelFontSize,
    fontFamily,
    fontWeight,
    margin,
    scaleMin,
    scaleStep,
    fillStyle,
    lineHeight,
    textAlign,
    strokeStyle,
    lineJoin,
    lineWidth,
    textBaseline,
    maxLines,
    pixelMultiplier,
    fitToPlane = false,
  } = options

  const logicalSize = 100
  const canvas = document.createElement("canvas")
  canvas.width = canvas.height = logicalSize * pixelMultiplier

  const ctx = canvas.getContext("2d")
  if (!ctx) throw new Error("GroundLabel: failed to create canvas context")

  ctx.scale(pixelMultiplier, pixelMultiplier)

  const applyFont = (fontSize: number) => {
    ctx.font = `${fontWeight} ${fontSize}px "${fontFamily}", Arial`
    ctx.textAlign = textAlign
    ctx.textBaseline = textBaseline
    ctx.fillStyle = fillStyle
    ctx.strokeStyle = strokeStyle
    ctx.lineJoin = lineJoin || "round"
    ctx.lineWidth = lineWidth || 0
  }

  const center = { x: logicalSize * 0.5, y: logicalSize * 0.5 }

  if (fitToPlane) {
    /**
     * fitToPlane mode (v1 behavior):
     * Always start from maxLabelFontSize and shrink down only if text overflows.
     * No wrapping — text stays on one line and fills as much width as possible.
     * Control size via maxLabelFontSize in config (default: 42).
     */
    let fontSize = maxLabelFontSize
    applyFont(fontSize)

    while (
      ctx.measureText(text).width + 2 * margin > logicalSize &&
      fontSize > minLabelFontSize
    ) {
      fontSize = Math.max(minLabelFontSize, fontSize - fontSize * scaleStep)
      applyFont(fontSize)
    }

    if (strokeStyle && lineWidth) ctx.strokeText(text, center.x, center.y)
    ctx.fillText(text, center.x, center.y)
  } else {
    /**
     * Default mode (v2 behavior):
     * Wrap text to fit within canvas width, then shrink font if it still overflows.
     * Better for occupant labels where text density matters more than size.
     */
    const initialFontSize = Math.max(
      minLabelFontSize,
      Math.min(requestedFontSize, maxLabelFontSize)
    )

    applyFont(initialFontSize)

    const maxWidth = logicalSize - 2 * margin
    let lines = wrapText(ctx, text, maxWidth, maxLines)
    let currentFontSize = initialFontSize
    let textWidth = max(lines.map((l) => ctx.measureText(l).width)) || 0

    while (
      currentFontSize > minLabelFontSize &&
      textWidth + 2 * margin > logicalSize
    ) {
      currentFontSize = Math.max(
        minLabelFontSize,
        currentFontSize - initialFontSize * scaleStep
      )
      applyFont(currentFontSize)
      lines = wrapText(ctx, text, maxWidth, maxLines)
      textWidth = max(lines.map((l) => ctx.measureText(l).width)) || 0
    }

    if (currentFontSize >= minLabelFontSize * scaleMin) {
      const totalHeight = lines.length * (currentFontSize * lineHeight)
      const startY =
        center.y - totalHeight / 2 + currentFontSize * lineHeight * 0.5

      lines.forEach((line, i) => {
        const y = startY + i * (currentFontSize * lineHeight)
        if (strokeStyle && lineWidth) ctx.strokeText(line, center.x, y)
        ctx.fillText(line, center.x, y)
      })
    }
  }

  const texture = new Texture(canvas)
  texture.needsUpdate = true

  return new MeshPhongMaterial({
    map: texture,
    transparent: true,
    alphaTest: 0.3,
  })
}

export const wrapText = (
  ctx: CanvasRenderingContext2D,
  value: string,
  maxWidth: number,
  maxLines: number
): string[] => {
  if (value.includes("\n")) return value.split(/\n/g).slice(0, maxLines)

  const words = value.trim().split(/\s+/)
  if (words.length <= 1) return [value]

  const lines: string[] = []
  let currentLine = words[0]

  for (let i = 1; i < words.length; i++) {
    const nextLine = `${currentLine} ${words[i]}`
    if (ctx.measureText(nextLine).width > maxWidth) {
      lines.push(currentLine)
      currentLine = words[i]
    } else {
      currentLine = nextLine
    }
  }

  lines.push(currentLine)
  return lines.slice(0, maxLines)
}

export const resolveLabelScale = (
  rectWidth: number,
  rectHeight: number,
  options: ResolveLabelScaleOptions
): number => {
  const { baseRectWidth, baseRectHeight, maxLabelScale } = options
  const scale = Math.min(rectWidth / baseRectWidth, rectHeight / baseRectHeight)
  return maxLabelScale && scale > maxLabelScale ? maxLabelScale : scale
}

const distanceSquared = (a: number[], b: number[]) =>
  (a[0] - b[0]) ** 2 + (a[1] - b[1]) ** 2

/**
 * Bridges a hole ring into an outer ring with a zero-width "keyhole" cut,
 * connecting the closest pair of points between the two. Ray-casting-based
 * point-in-polygon checks (used by d3plus-shape's largestRect) then treat
 * the hole's interior as outside the polygon, since the bridge is retraced
 * in both directions and contributes no net crossing.
 */
const bridgeHoleIntoRing = (ring: number[][], holeRing: number[][]) => {
  const hole = holeRing.slice(0, -1) // drop the closing duplicate point

  let minDist = Infinity
  let ringIndex = 0
  let holeIndex = 0

  for (let i = 0; i < ring.length; i++) {
    for (let j = 0; j < hole.length; j++) {
      const dist = distanceSquared(ring[i], hole[j])
      if (dist < minDist) {
        minDist = dist
        ringIndex = i
        holeIndex = j
      }
    }
  }

  const rotatedHole = [...hole.slice(holeIndex), ...hole.slice(0, holeIndex)]

  return [
    ...ring.slice(0, ringIndex + 1),
    ...rotatedHole,
    rotatedHole[0],
    ring[ringIndex],
    ...ring.slice(ringIndex + 1),
  ]
}

/**
 * Converts a polygon's rings (outer ring + hole rings, as in GeoJSON
 * Polygon.coordinates) into a single ring, bridging each hole into the
 * outer boundary. Lets algorithms that only understand simple rings (no
 * holes) correctly avoid hole areas.
 */
export const mergePolygonRings = (rings: number[][][]): number[][] => {
  const [outer, ...holes] = rings
  return holes.reduce(bridgeHoleIntoRing, outer)
}
